import { useAutoAnimate } from "@formkit/auto-animate/react";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  useMemberAsMemberNew,
  useMemberPaymentMethodListAsMember,
} from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { useQuery } from "@tanstack/react-query";
import { usePortalRoutes } from "apps/portal/src/hooks";
import { RouteFeature } from "apps/portal/src/routes";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { useAddPaymentMethodAlert } from "../../hooks/useAddPaymentMethodAlert";
import { useClubSettings } from "../../providers";
import { ApiResolverContext } from "../../providers/ApiResolverProvider/ApiResolverContext";
import { AuthenticationContext } from "../../providers/authentication/AuthenticationContext";
import useGymflowModels from "../../store";
import { Button, Spinner } from "../atoms";
import { EmptyPage } from "../atoms/EmptyPage";
import { PaymentMethodList } from "../UserMember/UserMemberPayments/PaymentMethodList";
import UserMemberWallet from "../UserMember/UserMemberPayments/UserMemberWallet";
import { HostedPagesBackHeader } from "./components/HostedPagesBackHeader";
import HostedPagesOverviewCard from "./components/HostedPagesOverviewCard";
import useRedirectIfNotAuthenticated from "./useRedirectIfNotAuthenticated";

export const HostedPagesProfilePayments = () => {
  const { t } = useTranslation();
  const { featureFlags } = useClubSettings();
  const { isEmbed } = usePortalRoutes();
  const { createSiteOrEmbedLink } = usePortalRoutes();
  useRedirectIfNotAuthenticated(
    createSiteOrEmbedLink(
      RouteFeature.SiteBuyMembershipType.replace(":membershipType", "all"),
    ),
  );
  const { userId: userMemberId } = useContext(AuthenticationContext);
  const { api } = useGymflowModels();
  const { clubId } = useContext(ApiResolverContext);
  const { data: userMember, isLoading: isLoadingUserMember } =
    useMemberAsMemberNew({ api });
  const { data: paymentMethods, isLoading: isLoadingPaymentMethods } =
    useMemberPaymentMethodListAsMember({
      api,
      memberId: userMemberId,
      clubId: clubId,
    });
  const isLoading = isLoadingUserMember || isLoadingPaymentMethods;
  const showAddPaymentMethodAlert = useAddPaymentMethodAlert({
    userMemberId: userMemberId,
    asMember: true,
  });

  const { data: currentWalletBalance, isLoading: isWalletBalanceLoading } =
    useQuery(apiV2.member.wallet.queries.list(userMemberId));

  const [parent] = useAutoAnimate();
  return (
    <div
      className={cn("track-height flex w-full flex-col overflow-y-auto", {
        "h-fit": isEmbed,
        "h-full": !isEmbed,
      })}
    >
      <HostedPagesBackHeader
        label={t("page.hostedPagesProfilePayments.header")}
        rightSideOptions={
          !isLoadingPaymentMethods &&
          paymentMethods &&
          paymentMethods.length > 0 ? (
            <Button
              intent="secondary-outline"
              className="items-center gap-2"
              onClick={() => {
                showAddPaymentMethodAlert();
              }}
            >
              <FontAwesomeIcon icon={faPlus} className="text-secondary-500" />
              <div className="max-lg:hidden">
                {t("pages.hostedPagesProfilePayments.addPM")}
              </div>
            </Button>
          ) : undefined
        }
      />
      <div
        ref={parent}
        className={cn(
          "flex bg-gray-50 dark:bg-gray-900 py-6 w-full flex-col lg:items-start lg:justify-center lg:flex-row",
          {
            "h-fit": isEmbed,
            "h-full": !isEmbed,
          },
        )}
      >
        {isLoading || isWalletBalanceLoading ? (
          <div className="flex size-full items-center justify-center py-20">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4 px-4 lg:flex-row lg:items-start">
            {featureFlags.FE_USER_WALLETS && userMemberId && (
              <HostedPagesOverviewCard.Container
                containerClassName="md:max-w-[32rem] p-0"
                innerContainerStyle="gap-2 p-0"
              >
                <UserMemberWallet
                  currentWalletBalance={currentWalletBalance}
                  link={createSiteOrEmbedLink(
                    RouteFeature.SiteProfileWalletHistory,
                  )}
                  memberId={userMemberId}
                />
              </HostedPagesOverviewCard.Container>
            )}

            <HostedPagesOverviewCard.Container
              containerClassName="md:max-w-[32rem] p-0 rounded-xl"
              innerContainerStyle="p-4 justify-start gap-2"
            >
              {userMember && paymentMethods?.length === 0 && (
                <EmptyPage
                  className="p-0"
                  textOverride={t(
                    "pages.hostedPagesProfilePayments.emptyPage.title",
                  )}
                  description={t(
                    "pages.hostedPagesProfilePayments.emptyPage.description",
                  )}
                  action={
                    <Button
                      intent="secondary"
                      className="items-center gap-2"
                      onClick={() => showAddPaymentMethodAlert()}
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      <div className="max-lg:hidden">
                        {t("pages.hostedPagesProfilePayments.addPM")}
                      </div>
                    </Button>
                  }
                />
              )}
              {userMember && paymentMethods && paymentMethods?.length > 0 && (
                <div className="dark:text-gray-0 flex w-full text-lg font-semibold text-gray-950">
                  {t("pages.hostedPagesProfilePayments.addedPM")}
                </div>
              )}
              {userMember && paymentMethods && paymentMethods?.length > 0 && (
                <PaymentMethodList
                  paymentMethods={paymentMethods}
                  userMember={userMember}
                  asMember={true}
                  className="static w-full p-0"
                />
              )}
            </HostedPagesOverviewCard.Container>
          </div>
        )}
      </div>
    </div>
  );
};
